/**
 * CL-ACCESS-FINANCIALS-001B — Staff financial and section permission constants.
 *
 * Client `canViewFinancials` is defined in clientPortalModuleApplicabilityModel.js
 * and must never grant staff/internal financial access.
 */

import { CLIENT_PORTAL_MODULE_PERMISSION_KEYS } from "./clientPortalModulePermissionKeys";
import { projectAccountingPermissionsUiEnabled } from "./projectAccountingPermissionsFeatureGate";

export const PROJECT_ACCESS_SCOPE_CUSTOM = "custom";

export const PROJECT_ACCESS_SCOPE_VALUES = Object.freeze([
  "full_project",
  "shared_files",
  "concept_only",
  "permit_drawings",
  PROJECT_ACCESS_SCOPE_CUSTOM,
]);

export const ASSIGNMENT_PRESET_ACCOUNTING = "accounting";

export const CLIENT_FINANCIAL_MODULE_PERMISSION_KEY =
  CLIENT_PORTAL_MODULE_PERMISSION_KEYS.financials;

export const SECTION_ACCESS_PERMISSION_KEYS = Object.freeze([
  "canAccessOverview",
  "canAccessClientDocs",
  "canAccessMasterFiles",
  "canAccessPlanning",
  "canAccessReview",
  "canAccessPermits",
  "canAccessConstruction",
  "canAccessTrades",
  "canAccessFinancials",
  "canAccessTimeLog",
]);

/** Editable in the collaborator permission editor; reserved keys stay stored but hidden. */
export const SECTION_ACCESS_EDITABLE_PERMISSION_KEYS = Object.freeze(
  SECTION_ACCESS_PERMISSION_KEYS.filter((key) => key !== "canAccessTrades")
);

export const CAN_ACCESS_TRADES_RESERVED_EDITOR_NOTE =
  "Trades (staff portal section) is reserved and not wired to a working staff portal tab yet. Stored values are preserved for compatibility.";

export const STAFF_FINANCIAL_VIEW_PERMISSION_KEYS = Object.freeze([
  "canViewInvoices",
  "canViewBudget",
  "canViewTimeLog",
]);

export const STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS = Object.freeze([
  "canManageBudget",
  "canManageTimeEntries",
]);

export const STAFF_INVOICE_WORKFLOW_PERMISSION_KEYS = Object.freeze([
  "canPrepareInvoices",
  "canReviewInvoices",
  "canApproveInvoices",
  "canShareClientTest",
  "canIssueInvoices",
  "canManageInvoiceSettings",
]);

export const RESERVED_STAFF_FINANCIAL_PERMISSION_KEYS = Object.freeze([
  "canViewReceipts",
  "canManageReceipts",
  "canViewPayments",
  "canRecordPayments",
]);

export const STAFF_FINANCIAL_PERMISSION_KEYS = Object.freeze([
  ...STAFF_FINANCIAL_VIEW_PERMISSION_KEYS,
  ...STAFF_FINANCIAL_MANAGE_PERMISSION_KEYS,
  ...STAFF_INVOICE_WORKFLOW_PERMISSION_KEYS,
  ...RESERVED_STAFF_FINANCIAL_PERMISSION_KEYS,
]);

export const LEGACY_INVOICE_PERMISSION_KEY = "canManageInvoices";

/** New keys only — legacy `canManageInvoices` remains in COLLABORATOR_PERMISSION_KEYS. */
export const ACCOUNTING_EXTENDED_PERMISSION_KEYS = Object.freeze([
  ...SECTION_ACCESS_PERMISSION_KEYS,
  ...STAFF_FINANCIAL_PERMISSION_KEYS,
]);

function safeStr(value) {
  return String(value ?? "").trim().toLowerCase();
}

export function isCustomAccessScope(accessScope) {
  return safeStr(accessScope) === PROJECT_ACCESS_SCOPE_CUSTOM;
}

export function isReservedStaffFinancialPermissionKey(key) {
  return RESERVED_STAFF_FINANCIAL_PERMISSION_KEYS.includes(key);
}

export function isStaffFinancialPermissionKey(key) {
  return STAFF_FINANCIAL_PERMISSION_KEYS.includes(key);
}

export function isSectionAccessPermissionKey(key) {
  return SECTION_ACCESS_PERMISSION_KEYS.includes(key);
}

export function readPermissionTriState(rawPermissions = {}, key = "") {
  if (!key) return "missing";
  if (rawPermissions == null || typeof rawPermissions !== "object") return "missing";
  if (typeof rawPermissions[key] !== "boolean") return "missing";
  return rawPermissions[key] ? "true" : "false";
}

export function readExplicitBoolean(rawPermissions = {}, key = "") {
  return readPermissionTriState(rawPermissions, key) === "true";
}

export function readExplicitFalse(rawPermissions = {}, key = "") {
  return readPermissionTriState(rawPermissions, key) === "false";
}

/**
 * Gate OFF: strip extended permission keys so current forms never write new fields.
 */
export function stripExtendedPermissionsForGateOffSave(
  permissions = {},
  { accountingPermissionsUiEnabled = null } = {}
) {
  if (projectAccountingPermissionsUiEnabled(accountingPermissionsUiEnabled)) {
    return { ...permissions };
  }
  const next = { ...permissions };
  ACCOUNTING_EXTENDED_PERMISSION_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
}
